/**
 * Single source of truth connecting frontend routes to the backend permission
 * keys returned by GET users/me/permissions.
 *
 * A requirement may be:
 *   - "Perm"                    one permission is required
 *   - ["A", "B"]                every permission is required
 *   - { allOf: [...] }          every permission is required
 *   - { anyOf: [...] }          at least one permission is required
 *   - { not: requirement }      inverted
 * Requirements nest, so { anyOf: ["A", { allOf: ["B", "C"] }] } is valid.
 */

export const ROUTE_PERMISSIONS = Object.freeze({
    "/ModelTraining": "ModelTraining",
    "/BasedOnTimeDatasetCreating": "BasedOnTimeDatasetCreating",
    "/FileFilter": "FileFilter",

    "/ReviewNonMetricSpace": "WordCloudDisplay_VocabularyVectorSpace",
    "/ReviewNonMetricSpace/:extension": "WordCloudDisplay_VocabularyVectorSpace",

    "/ReviewDataAndDefineConcepts": "VocabularyNeighborhood-DistanceBetweenConcepts",
    "/ReviewMetricSpace": "CreatingAGeometricMapBasedOnVocabularyMarking",
    "/ReviewTopicalMetricSpace": "CreatingAGeometricMapBasedOnThematicAnalysis",

    "/ProportionalityBetweenConcepts": "ProportionalityBetweenConcepts",
    "/WordSuggestions": "WordSuggestion",
    "/DesignMultipleChoiceQuestions": "MultipleChoiceQuestions",

    "/LexicalSemantic": "LexicalSemantic",

    "/CommunityAnalysis": "IdentifyingUserCommunities",
    "/TopicChoose": "IdentifyingUserCommunities",
    "/TopicRecognitionNew": "IdentifyingUserCommunities",
    "/TopicRecognitionBarchart": "ThematicAnalysisWithTheEliminationOfStopWords",
    "/TopicRecognitionBarchartWithoutStopWords":
        "ThematicAnalysisWithTheEliminationOfStopWords",
    "/InfluentialUsersRecognition": "IdentifyingInfluentialUsers",
    "/DeepInfluentialUsersRecognition": "DeepIdentificationOfInfluentialUsers",
    "/PezvakRoom": "PezvakRoom",
    "/TopicalAnalysisOverTime": "ThematicAnalysisOverTime",
    "/TopicRecognitionBubble": "AnalysisOfTheRelationshipBetweenTopics",
    "/TopicRecognitionSentence": "AnalysisOfTheRelationshipBetweenTopics",
    "/TopicRecognitionDotplot": "AnalyzingTheRelationshipBetweenCommunities",
    "/TopicRecognitionBubbleAndSingleDotplot":
        "IdentifyingAndAnalyzingIntraAndExtraTopicClusters",
    "/TopicRecognitionTopicsDateDotplot": "IdentifyingFakeNewsAndHotTopics",
    "/TopicRecognitionTopicsUsersDotplot":
        "IdentifyingPeopleWhoAreMentallyRelatedToEachother",

    "/EstimatingBiasWithSupervision": "BiasEstimationWithSupervision",
    "/EstimatingBiasUnsupervised": "BiasEstimationWithoutSupervision",
    "/ReviewCognitiveBias": {
        anyOf: [
            "BiasEstimationWithSupervision",
            "BiasEstimationWithoutSupervision",
        ],
    },

    "/TopicalMetricSpaceComparison":
        "ComparingCognitiveSpacesBasedOnSimpleThematicAnalysis",
    "/TopicalWeightedMetricSpaceComparison":
        "ComparingCognitiveSpacesBasedOnWeightedThematicAnalysis",
    "/VocabularyMetricSpaceComparison":
        "ComparingCognitiveSpacesBasedOnWordRepresentation",

    "/CreatePerceptionRequest": "ConstructingPerceptualAnalysis",
    "/ExaminingHypothesesAndQuestionsInDocuments": "CheckingQuestionsInDocuments",
    "/CheckingAssumptionsInDocs": "CheckingAssumptionsInDocuments",
    "/ReviewRelatedDocuments": "ReviewRelatedDocuments",
    "/ManagementSummary": "ManagementSummary",
    "/GeneralOverviewDocument": "GeneralViewOfTheDocuments",
    "/Tagging": "Labeling",
    "/ThinkThankAffects": "TheInfluenceOfThinkTanks",

    "/CredibleNarratives": "ProducingBelievableNarrative",
    "/UsersProfile": "CreatingUserProfiles",
    "/Survey": "Survey",
    "/NationalSurvey": "Validation",

    "/TwoLayerSorting": "TwoLayerClassification",
    "/WordsSuggestion": "VocabularySuggestion",
    "/UserDashboard": "UserDashboard",
    "/EnvManagement": "EnvManagement",

    "/ProductionNews": "ProductionNews"
});

/**
 * Routes that are deliberately reachable by any authenticated user.
 *
 * This list is what makes fail-closed behaviour possible: anything that is
 * neither listed here nor in ROUTE_PERMISSIONS is treated as UNKNOWN and is
 * hidden/blocked rather than silently allowed. Adding a page therefore forces
 * a conscious choice — protect it, or declare it public here.
 */
export const PUBLIC_ROUTES = Object.freeze(
    new Set([
        "/",
        "/dashboard-page",
        "/login",
        "/login-page",
        "/logout",
        "/auth/signin",
        "/intro",
        "/help",
        "/qa",
        "/contactus",
        "/userprofile",
        "/usersettings",
        "/userrequest",
        "/useradmin",
        "/serviceadmin",
        "/messageDesign",
        "/messageStudy",
    ])
);

export const ACCESS_PUBLIC = "public";
export const ACCESS_PROTECTED = "protected";
export const ACCESS_UNKNOWN = "unknown";

const IS_DEV = process.env.NODE_ENV !== "production";

/* ------------------------------------------------------------------ */
/*  path handling                                                      */
/* ------------------------------------------------------------------ */

/**
 * Reduces any href/link/to value to a comparable pathname.
 * Handles absolute URLs, query strings, hashes and trailing slashes.
 */
export function normalizeRoutePath(path) {
    if (typeof path !== "string") return null;

    let value = path.trim();
    if (!value) return null;

    // Absolute and protocol-relative URLs. Anything pointing at another origin
    // is not one of our routes, so it stays unresolved and therefore denied
    // unless the menu item declares itself public. Note that "//evil.com"
    // is a protocol-relative URL, not the path "/evil.com".
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith("//")) {
        const origin =
            typeof window !== "undefined" && window.location
                ? window.location.origin
                : undefined;

        try {
            const parsed = new URL(value, origin);
            if (origin && parsed.origin !== origin) return null;
            value = parsed.pathname;
        } catch {
            return null;
        }
    }

    value = value.split("?")[0].split("#")[0];
    if (!value) return "/";

    try {
        value = decodeURI(value);
    } catch {
        /* keep the raw value when it is not valid percent-encoding */
    }

    if (!value.startsWith("/")) value = `/${value}`;

    // Collapse duplicate slashes and drop the trailing one (except at root).
    value = value.replace(/\/{2,}/g, "/");
    if (value.length > 1) value = value.replace(/\/+$/, "");

    return value || "/";
}

/** Turns "/ReviewNonMetricSpace/:extension" into a matcher. */
function patternToRegExp(pattern) {
    const source = pattern
        .split("/")
        .map((segment) => {
            if (segment.startsWith(":")) return "[^/]+";
            if (segment === "*") return ".*";
            return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        })
        .join("/");

    return new RegExp(`^${source}/?$`);
}

// Dynamic patterns, most specific first (fewest params, then longest).
const DYNAMIC_PATTERNS = Object.keys(ROUTE_PERMISSIONS)
    .filter((key) => key.includes(":") || key.includes("*"))
    .sort((a, b) => {
        const paramDiff =
            (a.match(/:/g) || []).length - (b.match(/:/g) || []).length;
        return paramDiff !== 0 ? paramDiff : b.length - a.length;
    })
    .map((pattern) => ({ pattern, regexp: patternToRegExp(pattern) }));

// Case-insensitive index used only to catch casing typos (see below).
const LOWERCASE_INDEX = new Map(
    Object.keys(ROUTE_PERMISSIONS).map((key) => [key.toLowerCase(), key])
);
const LOWERCASE_PUBLIC = new Set(
    Array.from(PUBLIC_ROUTES, (key) => key.toLowerCase())
);

const warnedOnce = new Set();
function warnOnce(message) {
    if (!IS_DEV || warnedOnce.has(message)) return;
    warnedOnce.add(message);
    console.warn(`[permissions] ${message}`);
}

/**
 * Resolves a path to an access decision.
 *
 * Returns { status, requirement, matchedPath } where status is one of
 * ACCESS_PUBLIC, ACCESS_PROTECTED or ACCESS_UNKNOWN. Callers must treat
 * ACCESS_UNKNOWN as "deny" — that is the whole point of this function.
 */
export function resolveRouteAccess(path) {
    const normalizedPath = normalizeRoutePath(path);

    if (!normalizedPath) {
        return {
            status: ACCESS_UNKNOWN,
            requirement: null,
            matchedPath: null,
            normalizedPath: null,
        };
    }

    if (PUBLIC_ROUTES.has(normalizedPath)) {
        return {
            status: ACCESS_PUBLIC,
            requirement: null,
            matchedPath: normalizedPath,
            normalizedPath,
        };
    }

    const exact = ROUTE_PERMISSIONS[normalizedPath];
    if (exact) {
        return {
            status: ACCESS_PROTECTED,
            requirement: exact,
            matchedPath: normalizedPath,
            normalizedPath,
        };
    }

    for (const { pattern, regexp } of DYNAMIC_PATTERNS) {
        if (regexp.test(normalizedPath)) {
            return {
                status: ACCESS_PROTECTED,
                requirement: ROUTE_PERMISSIONS[pattern],
                matchedPath: pattern,
                normalizedPath,
            };
        }
    }

    // Casing mismatches lock real users out of pages they paid for, so we
    // still resolve them, but loudly, so the typo gets fixed.
    const lowered = normalizedPath.toLowerCase();
    if (LOWERCASE_PUBLIC.has(lowered)) {
        warnOnce(
            `"${normalizedPath}" only matches a public route by casing. Fix the casing in the menu or in PUBLIC_ROUTES.`
        );
        return {
            status: ACCESS_PUBLIC,
            requirement: null,
            matchedPath: normalizedPath,
            normalizedPath,
        };
    }

    const loweredMatch = LOWERCASE_INDEX.get(lowered);
    if (loweredMatch) {
        warnOnce(
            `"${normalizedPath}" only matches "${loweredMatch}" by casing. Fix the casing in the menu or in ROUTE_PERMISSIONS.`
        );
        return {
            status: ACCESS_PROTECTED,
            requirement: ROUTE_PERMISSIONS[loweredMatch],
            matchedPath: loweredMatch,
            normalizedPath,
        };
    }

    return {
        status: ACCESS_UNKNOWN,
        requirement: null,
        matchedPath: null,
        normalizedPath,
    };
}

/**
 * Kept for backwards compatibility with existing call sites.
 * Prefer resolveRouteAccess: this cannot distinguish "public" from "unmapped".
 */
export function getRoutePermission(path) {
    return resolveRouteAccess(path).requirement;
}

/** True when the path is a route we know nothing about. */
export function isUnmappedRoute(path) {
    return resolveRouteAccess(path).status === ACCESS_UNKNOWN;
}

/* ------------------------------------------------------------------ */
/*  requirement evaluation                                             */
/* ------------------------------------------------------------------ */

export function satisfiesPermissionRequirement(requirement, hasPermission) {
    if (requirement == null) return true;

    if (typeof requirement === "string") {
        return hasPermission(requirement);
    }

    if (Array.isArray(requirement)) {
        return requirement.every((entry) =>
            satisfiesPermissionRequirement(entry, hasPermission)
        );
    }

    if (typeof requirement !== "object") return false;

    if (Array.isArray(requirement.anyOf)) {
        return requirement.anyOf.some((entry) =>
            satisfiesPermissionRequirement(entry, hasPermission)
        );
    }

    if (Array.isArray(requirement.allOf)) {
        return requirement.allOf.every((entry) =>
            satisfiesPermissionRequirement(entry, hasPermission)
        );
    }

    if ("not" in requirement) {
        return !satisfiesPermissionRequirement(requirement.not, hasPermission);
    }

    // Unrecognised shape: deny rather than guess.
    warnOnce(
        `Unrecognised requirement shape: ${JSON.stringify(requirement)}. Access denied.`
    );
    return false;
}

/* ------------------------------------------------------------------ */
/*  development-time coverage check                                    */
/* ------------------------------------------------------------------ */

/**
 * Reports paths that are neither protected nor explicitly public.
 * Call it once at startup with your route paths to catch a missing mapping
 * before it reaches a user.
 */
export function auditRouteCoverage(paths = []) {
    const unmapped = Array.from(new Set(paths))
        .filter((path) => typeof path === "string" && path !== "*")
        .filter((path) => resolveRouteAccess(path).status === ACCESS_UNKNOWN);

    if (IS_DEV && unmapped.length) {
        console.warn(
            "[permissions] These paths have no entry in ROUTE_PERMISSIONS and are not in PUBLIC_ROUTES. They will be blocked:",
            unmapped
        );
    }

    return unmapped;
}
